import { useCallback, useMemo, useRef } from "react";
import type { EmployeeCategory } from "@/interface/employeeOnBoard";
import { useMasterData } from "./useMasterData";
import { useEmployeeTab } from "./useEmployeeTab";
import { useSalaryConfigTab } from "./useSalaryConfigTab";
import { useAttendanceTab } from "./useAttendanceTab";
import { useSalaryPreviewTab } from "./useSalaryPreviewTab";
import { useEmployeeSelection } from "./useEmployeeSelection";
import { useFileOperations } from "./useFileOperations";

export { CATEGORY_OPTIONS } from "./createSalary.constants";

/**
 * Orchestrator hook — wires all domain hooks together and returns
 * the same flat API that CreateSalary.tsx has always consumed.
 *
 * Hook call order guarantees no Rules of Hooks violations:
 *   1. masterData         (independent)
 *   2. employeeTab        (depends on masterData)
 *   3. attendanceTab      (depends on masterData + selectionRef)
 *   4. selection           (depends on masterData + employeeTab.employees + attendanceTab.attendanceList)
 *   5. salaryConfigTab    (depends on masterData)
 *   6. salaryPreviewTab   (depends on masterData + selectionRef)
 *   7. fileOps            (depends on masterData + selection + refetch callbacks)
 */
export const useCreateSalary = () => {
  // Stable ref for selectedEmployeeIds — breaks circular dep
  // between selection ↔ attendance/salary-preview hooks.
  const selectedEmployeeIdsRef = useRef<Set<string | number>>(new Set());

  // ── 1. Shared filters ──
  const masterData = useMasterData();

  // ── 2. Employee list tab ──
  const employeeTab = useEmployeeTab(masterData);

  // ── 3. Attendance tab (uses ref for lock handler) ──
  const attendanceTab = useAttendanceTab(masterData, selectedEmployeeIdsRef);

  // ── 4. Employee selection ──
  const selection = useEmployeeSelection(
    masterData,
    employeeTab.employees,
    attendanceTab.attendanceList,
  );
  // Keep ref in sync with latest selection
  selectedEmployeeIdsRef.current = selection.selectedEmployeeIds;

  // ── 5. Salary config tab ──
  const salaryConfigTab = useSalaryConfigTab(masterData);

  const handleSalaryCreatedSuccess = useCallback(() => {
    selection.clearSelection();
    attendanceTab.refetchAttendanceSummary();
    employeeTab.refetchEmployees();
  }, [
    selection.clearSelection,
    attendanceTab.refetchAttendanceSummary,
    employeeTab.refetchEmployees,
  ]);

  // ── 6. Salary preview tab (uses ref) ──
  const salaryPreviewTab = useSalaryPreviewTab(
    masterData,
    selectedEmployeeIdsRef,
    handleSalaryCreatedSuccess,
  );

  // ── 7. File operations ──
  const refetchCallbacks = useMemo(
    () => ({
      refetchEmployees: employeeTab.refetchEmployees,
      refetchBranchWiseUserSalary: salaryConfigTab.refetchBranchWiseUserSalary,
      refetchAttendanceSummary: attendanceTab.refetchAttendanceSummary,
      refetchSalaryPreview: salaryPreviewTab.refetchSalaryPreview,
    }),
    [
      employeeTab.refetchEmployees,
      salaryConfigTab.refetchBranchWiseUserSalary,
      attendanceTab.refetchAttendanceSummary,
      salaryPreviewTab.refetchSalaryPreview,
    ],
  );

  const fileOps = useFileOperations(
    masterData,
    selection.selectedEmployeeIds,
    selection.isAllBranchUsersSelected,
    refetchCallbacks,
  );

  // ── Cross-cutting handlers: filter change → reset all pagination + selection ──

  const handleBranchSelect = useCallback(
    (branchId: string) => {
      masterData.handleBranchSelect(branchId);
      employeeTab.resetPagination();
      attendanceTab.resetPagination();
      salaryPreviewTab.resetPagination();
      salaryConfigTab.resetPagination();
      selection.clearSelection();
    },
    [
      masterData.handleBranchSelect,
      employeeTab.resetPagination,
      attendanceTab.resetPagination,
      salaryPreviewTab.resetPagination,
      salaryConfigTab.resetPagination,
      selection.clearSelection,
    ],
  );

  const handleSiteSelect = useCallback(
    (siteId: string) => {
      masterData.handleSiteSelect(siteId);
      employeeTab.resetPagination();
      attendanceTab.resetPagination();
      salaryPreviewTab.resetPagination();
      salaryConfigTab.resetPagination();
      selection.clearSelection();
    },
    [
      masterData.handleSiteSelect,
      employeeTab.resetPagination,
      attendanceTab.resetPagination,
      salaryPreviewTab.resetPagination,
      salaryConfigTab.resetPagination,
      selection.clearSelection,
    ],
  );

  const handleCategoryChange = useCallback(
    (category: EmployeeCategory) => {
      masterData.handleCategoryChange(category);
      employeeTab.resetPagination();
      attendanceTab.resetPagination();
      salaryPreviewTab.resetPagination();
      salaryConfigTab.resetPagination();
      selection.clearSelection();
    },
    [
      masterData.handleCategoryChange,
      employeeTab.resetPagination,
      attendanceTab.resetPagination,
      salaryPreviewTab.resetPagination,
      salaryConfigTab.resetPagination,
      selection.clearSelection,
    ],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      masterData.handleSearchChange(query);
      employeeTab.resetPagination();
      attendanceTab.resetPagination();
      salaryPreviewTab.resetPagination();
      salaryConfigTab.resetPagination();
    },
    [
      masterData.handleSearchChange,
      employeeTab.resetPagination,
      attendanceTab.resetPagination,
      salaryPreviewTab.resetPagination,
      salaryConfigTab.resetPagination,
    ],
  );

  const handleAttendanceDateRangeChange = useCallback(
    (start: string, end: string) => {
      masterData.handleAttendanceDateRangeChange(start, end);
      attendanceTab.resetPagination();
      salaryPreviewTab.resetPagination();
      selection.clearSelection();
    },
    [
      masterData.handleAttendanceDateRangeChange,
      attendanceTab.resetPagination,
      salaryPreviewTab.resetPagination,
      selection.clearSelection,
    ],
  );

  // ── Return the same flat API as before — zero consumer changes ──
  return {
    // Master data state
    branches: masterData.branches,
    selectedBranchId: masterData.selectedBranchId,
    isLoadingBranches: masterData.isLoadingBranches,
    availableSites: masterData.availableSites,
    selectedSiteId: masterData.selectedSiteId,
    isLoadingSites: masterData.isLoadingSites,
    selectedCategory: masterData.selectedCategory,
    activeTab: masterData.activeTab,
    setActiveTab: masterData.handleTabChange,
    searchValue: masterData.searchValue,
    attendanceStartDate: masterData.attendanceStartDate,
    attendanceEndDate: masterData.attendanceEndDate,

    // Employee tab
    employees: employeeTab.employees,
    isLoadingEmployees: employeeTab.isLoadingEmployees,
    paginationData: employeeTab.paginationData,
    setPaginationData: employeeTab.setPaginationData,
    handleChangePageSize: employeeTab.handleChangePageSize,

    // Salary config tab
    branchesWiseSalaryHead: salaryConfigTab.branchesWiseSalaryHead,
    salaryConfigData: salaryConfigTab.salaryConfigData,
    isLoadingSalaryConfig: salaryConfigTab.isLoadingSalaryConfig,
    salaryConfigPaginationData: salaryConfigTab.salaryConfigPaginationData,
    setSalaryConfigPaginationData:
      salaryConfigTab.setSalaryConfigPaginationData,
    handleSalaryConfigChangePageSize:
      salaryConfigTab.handleSalaryConfigChangePageSize,
    handleSaveUserSalary: salaryConfigTab.handleSaveUserSalary,

    // Attendance tab
    attendanceList: attendanceTab.attendanceList,
    isLoadingAttendance: attendanceTab.isLoadingAttendance,
    isAttendanceLocked: attendanceTab.isAttendanceLocked,
    isLockingAttendance: attendanceTab.isLockingAttendance,
    showLockAttendanceModal: attendanceTab.showLockAttendanceModal,
    attendancePaginationData: attendanceTab.attendancePaginationData,
    setAttendancePaginationData: attendanceTab.setAttendancePaginationData,
    handleAttendanceChangePageSize:
      attendanceTab.handleAttendanceChangePageSize,
    handleLockAttendance: attendanceTab.handleLockAttendance,
    handleOpenLockAttendanceModal:
      attendanceTab.handleOpenLockAttendanceModal,
    handleCloseLockAttendanceModal:
      attendanceTab.handleCloseLockAttendanceModal,
    handleConfirmLockAttendance: attendanceTab.handleConfirmLockAttendance,
    handleDownloadSalarySlip: attendanceTab.handleDownloadSalarySlip,
    downloadingSalaryId: attendanceTab.downloadingSalaryId,

    // Salary preview tab
    salaryPreviewData: salaryPreviewTab.salaryPreviewData,
    salaryPreviewUsers: salaryPreviewTab.salaryPreviewUsers,
    salaryPreviewErrors: salaryPreviewTab.salaryPreviewErrors,
    paginatedSalaryData: salaryPreviewTab.paginatedSalaryData,
    isLoadingSalaryPreview: salaryPreviewTab.isLoadingSalaryPreview,
    isCreatingSalary: salaryPreviewTab.isCreatingSalary,
    showCreateSalaryConfirmModal: salaryPreviewTab.showCreateSalaryConfirmModal,
    salaryPreviewPaginationData: salaryPreviewTab.salaryPreviewPaginationData,
    setSalaryPreviewPaginationData:
      salaryPreviewTab.setSalaryPreviewPaginationData,
    salarySubTab: salaryPreviewTab.salarySubTab,
    setSalarySubTab: salaryPreviewTab.setSalarySubTab,
    handleSalaryPreviewChangePageSize:
      salaryPreviewTab.handleSalaryPreviewChangePageSize,
    handleCreateMonthlySalary: salaryPreviewTab.handleCreateMonthlySalary,
    openCreateSalaryConfirmModal: salaryPreviewTab.openCreateSalaryConfirmModal,
    closeCreateSalaryConfirmModal:
      salaryPreviewTab.closeCreateSalaryConfirmModal,
    handleConfirmCreateSalary: salaryPreviewTab.handleConfirmCreateSalary,
    selectedUserForAddHead: salaryPreviewTab.selectedUserForAddHead,
    showAddSalaryHeadModal: salaryPreviewTab.showAddSalaryHeadModal,
    handleOpenAddSalaryHeadModal: salaryPreviewTab.handleOpenAddSalaryHeadModal,
    handleCloseAddSalaryHeadModal:
      salaryPreviewTab.handleCloseAddSalaryHeadModal,
    handleAddSalaryHead: salaryPreviewTab.handleAddSalaryHead,
    handleRemoveSalaryHead: salaryPreviewTab.handleRemoveSalaryHead,

    // Selection
    selectedEmployeeIds: selection.selectedEmployeeIds,
    isAllBranchUsersSelected: selection.isAllBranchUsersSelected,
    selectionType: selection.selectionType,
    isSelectingUsers: selection.isSelectingUsers,
    handleSelectAll: selection.handleSelectAll,
    handleAttendanceSelectAll: selection.handleAttendanceSelectAll,
    handleSelectItem: selection.handleSelectItem,
    handleSelectUsersByMode: selection.handleSelectUsersByMode,
    handleToggleSelectAllBranchUsers:
      selection.handleToggleSelectAllBranchUsers,

    // File operations
    selectedFile: fileOps.selectedFile,
    isDownloading: fileOps.isDownloading,
    isUploading: fileOps.isUploading,
    showUploadConfirmModal: fileOps.showUploadConfirmModal,
    fileInputRef: fileOps.fileInputRef,
    canDownloadTemplate: fileOps.canDownloadTemplate,
    handleDownloadTemplate: fileOps.handleDownloadTemplate,
    handleFileChange: fileOps.handleFileChange,
    handleClearFile: fileOps.handleClearFile,
    handleUploadFile: fileOps.handleUploadFile,
    handleOpenUploadConfirmModal: fileOps.handleOpenUploadConfirmModal,
    handleCloseUploadConfirmModal: fileOps.handleCloseUploadConfirmModal,
    handleConfirmUploadFile: fileOps.handleConfirmUploadFile,

    // Cross-cutting handlers
    handleBranchSelect,
    handleSiteSelect,
    handleCategoryChange,
    handleSearchChange,
    handleAttendanceDateRangeChange,

    // Refetch helpers
    refetchEmployees: employeeTab.refetchEmployees,
    refetchBranchWiseUserSalary: salaryConfigTab.refetchBranchWiseUserSalary,
    refetchAttendanceSummary: attendanceTab.refetchAttendanceSummary,
    refetchSalaryPreview: salaryPreviewTab.refetchSalaryPreview,
  };
};
